import React, { useState, useContext } from "react";
import { updateRecordHandHygiene } from "../../../service/endpoint-manager";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Button,
  Dialog,
} from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import HHSingleForm from "./HHsingleForm";
import clsx from "clsx";
import { Typography } from "@material-ui/core";

import { viewportContext } from "../../../context/ViewportContext";
import styles from "../../../styles.module.css";
import exportPDF from "./recordsExportPDF";
import { exportCSV } from "./recordsExportCSV";

const HandHygieneRecords = ({ records, loading, error, setRecords }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const { screenType } = useContext(viewportContext);

  const formatOutcome = (outcome, timer) => {
    return timer > 0 ? `${outcome} (${timer} sec.)` : outcome;
  };

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNewObservationClick = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleExportPDF = () => {
    exportPDF(records, formatOutcome);
  };

  const copyToClipboard = (record) => {
    const recordData = `
        Observer: ${record.nurse}
        HCW Type: ${record.formQuestions.healthCareWorker}
        Moment: ${record.formQuestions.handHygieneMoment}
        Outcome: ${formatOutcome(
          record.formQuestions.outcome,
          record.formQuestions.timer
        )}
        Rings: ${record.formQuestions.custom.rings ? "Yes" : "No"}
        Gloves: ${record.formQuestions.custom.gloves ? "Yes" : "No"}
        Nails: ${record.formQuestions.custom.nails ? "Yes" : "No"}
        Bracelet: ${record.formQuestions.custom.bracelet ? "Yes" : "No"}
        Created By: ${record.createdBy}
        Created Date: ${new Date(record.createdAt).toLocaleString()}
        Comments: ${record.formQuestions.comment}
        `;
    navigator.clipboard
      .writeText(recordData)
      .then(() => {})
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  const handleDelete = async (recordId) => {
    try {
      const response = await updateRecordHandHygiene(recordId, {
        deleted: true,
      });
      if (response.status === 200) {
        setRecords(records.filter((record) => record.id !== recordId));
      } else {
        console.error("Failed to delete the record", response.data);
      }
    } catch (error) {
      console.error(
        "Error deleting the record:",
        error.response ? error.response.data : error
      );
    }
  };

  const handleExportCSV = () => {
    exportCSV(records, formatOutcome);
  };
  return (
    <>
      <Paper className={styles.paperStyles}>
        <div className={styles.titleButtonRow}>
          <Typography variant="h5">Observations</Typography>
          <div className={styles.exportButtonContainer}>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.primaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() => handleNewObservationClick(false)}
              >
                {screenType.isMobile ? "Create" : "+ New Observation"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.secondaryButton,
                  styles.complianceTableButtons
                )}
                onClick={handleExportPDF}
              >
                Export to PDF
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.secondaryButton,
                  styles.complianceTableButtons
                )}
                onClick={handleExportCSV}
              >
                Export to CSV
              </Button>
              <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="lg"
                fullWidth
                className="handHygieneDialogSingleForm"
              >
                <HHSingleForm />
              </Dialog>
            </div>
            <div>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={records.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className={styles.tablePagination}
              />
            </div>
          </div>
        </div>

        <TableContainer className={styles.obsverationTableContainer}>
          <Table stickyHeader aria-label="hand hygiene records">
            <TableHead>
              <TableRow>
                <TableCell>Observer</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>HCW Type</TableCell>
                <TableCell>Moment</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Rings</TableCell>
                <TableCell>Gloves</TableCell>
                <TableCell>Nails</TableCell>
                <TableCell>Bracelet</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.nurse}</TableCell>
                    <TableCell>{record.facility}</TableCell>
                    <TableCell>{record.unit}</TableCell>
                    <TableCell>
                      {record.formQuestions.healthCareWorker}
                    </TableCell>
                    <TableCell>
                      {record.formQuestions.handHygieneMoment}
                    </TableCell>
                    <TableCell>
                      {formatOutcome(
                        record.formQuestions.outcome,
                        record.formQuestions.timer
                      )}
                    </TableCell>
                    <TableCell>
                      {record.formQuestions.custom.rings ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>
                      {record.formQuestions.custom.gloves ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>
                      {record.formQuestions.custom.nails ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>
                      {record.formQuestions.custom.bracelet ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>
                      {new Date(record.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>{record.formQuestions.comment}</TableCell>
                    <TableCell>
                      <div className={styles.flexContainer}>
                        <IconButton
                          onClick={() => copyToClipboard(record)}
                          aria-label="copy"
                        >
                          <FileCopyIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(record.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default HandHygieneRecords;
